import React, {useContext, useLayoutEffect, useState} from 'react';
import {Platform, Text} from 'react-native';
import {StoreContext} from "../../../../../components/StoreContextProvider";
import * as Yup from "yup";
import {useFormik} from "formik";
import Input from "../../../../../components/Input";
import MainStyle from "../../../../../../MainStyle";
import {dateFormat, isFullSwing, phoneUtil, StartingPage} from '../../../../../constants';
import {colors} from "../../../../../assets/styles/colors";
import Button from "../../../../../components/Button";
import {profanity} from '@2toad/profanity';
import PhoneInput from "../../../../../components/PhoneInput";
import Handedness from "../../../../../components/Handedness";
import {useNavigation} from "@react-navigation/native";
import ServiceClient from "../../../../../utils/ServiceClient";
import {CancelModalProps, updateUserPayloadType} from "../../../../../types";
import OtpModal from "../../../../../modals/OtpModal";
import {ChangeType, Gender} from "../../../../../../generated/smg_service_pb";
import moment, {Moment} from "moment";
import {getFieldError} from "../../../../../utils";
import {LATIN_KEYBOARD_MESSAGE, LATIN_KEYBOARD_REGEX} from "../../../../../utils/inputValidation";
import Div from "../../../../../lib/Div";
import {LinearGradient} from "expo-linear-gradient";
import FullSwingProfileField from "../../../../../components/FullSwingProfileField";
import P from "../../../../../lib/P";
import Btn from "../../../../../components/Btn";
import Loader from "../../../../../components/Loader";
import CancelModal from "../../../../Auth/SignUp/steps/CancelModal";

type InitialValuesType = {
  email: string, phone: string, username: string, handedness: string, dateOfBirth: Moment | null, firstname: string,
  lastname: string, gender: Gender
}

const initialValues: InitialValuesType = {
  email: '', phone: '', username: '', handedness: '', firstname: '', lastname: '', dateOfBirth: null,
  gender: Gender.UNSPECIFIED
};

const Profile = () => {
  const Context = useContext(StoreContext);
  const {user} = Context;
  const formik = useFormik({
    initialValues, validationSchema: Yup.object({
      email: Yup.string().email().required(), phone: Yup.string(),
      username: Yup.string().required('Player Name is a required field')
        .max(12, 'Screen Name must be 12 characters or less.')
        .matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE),
      handedness: Yup.string(),
      firstname: Yup.string().matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE),
      lastname: Yup.string().matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE),
      dateOfBirth: Yup.mixed<Moment>().nullable().required('A valid date of birth is required.')
        .test('age-min', 'You must be at least 18 years old.', (value) => {
          if (!value) return false;
          const age = new Date().getFullYear() - value.year();
          return age >= 18;
        })
        .test('age-max', 'Please enter a valid date of birth.', (value) => {
          if (!value) return false;
          const age = new Date().getFullYear() - value.year();
          return age <= 100;
        }),
      gender: Yup.mixed<Gender>()
    }),
    onSubmit: (values) => {
      if (values.phone.trim() !== '') {
        let isValid = false;

        try {
          isValid = phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(values.phone));

        } catch (error) {
          console.log('isValidNumber error', error);
        }

        if (!isValid) {
          formik.setFieldError('phone', 'Invalid format');
          setCancelModalConfig({
            visible: true,
            title: 'Invalid Phone Number',
            subtitle: 'Please enter a valid phone number or leave it empty.',
            leftBtnText: 'OK',
            onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
          });
          return;
        }
      }

      if (profanity.exists(values.username)) {
        formik.setFieldError('username', 'The selected user name is not allowed. Please choose another username');
        return;
      }

      onSave();
    }
  });
  const [init, setInit] = useState(false);
  const navigation: any = useNavigation();
  const [phoneVerificationDialog, setPhoneVerificationDialog] = useState(false);
  const isKycCompleted = user?.kycStatus === 1;
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [cancelModalConfig, setCancelModalConfig] = useState<CancelModalProps>({visible: false, title: '', subtitle: ''});

  const requestChange = (type: ChangeType) => () => {
    setLoadingOverlay(true);

    ServiceClient.requestChange(type).then(() => {
      setLoadingOverlay(false);

      setCancelModalConfig({
        visible: true, title: 'Success', titleColor: MainStyle.c_green, leftBtnText: 'OK',
        subtitle: `You have successfully sent a request to change your ${type === ChangeType.ADDRESS ? 'address' : 'photo'} to the support team.`,
        onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
      });

    }).catch(err => {
      setLoadingOverlay(false);
      console.log('requestChange err', err);

      setCancelModalConfig({
        visible: true, subtitle: err.message || 'requestChange Error', title: 'Error', leftBtnText: 'OK',
        onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const updatePhoneNumber = (code?: string) => {
    setPhoneVerificationDialog(false);
    setLoadingOverlay(true);

    ServiceClient.updatePhoneNumber(formik.values.phone, code).then(() => {
      setLoadingOverlay(false);

    }).catch(err => {
      setLoadingOverlay(false);
      console.log('updatePhoneNumber err', err);

      setCancelModalConfig({
        visible: true, subtitle: err.message || 'UpdatePhoneNumber Error', title: 'Error', leftBtnText: 'OK',
        onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const requestOtpCode = () => {
    setLoadingOverlay(true);

    ServiceClient.requestOtpCode({phone: formik.values.phone}).then(() => {
      setLoadingOverlay(false);
      setPhoneVerificationDialog(true);

    }).catch(err => {
      setLoadingOverlay(false);
      console.log('requestOtpCode err', err);

      setCancelModalConfig({
        visible: true, subtitle: err.message || 'requestOtpCode Error', title: 'Error', leftBtnText: 'OK',
        onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const deactivateAccountHandler = () => {
    setCancelModalConfig({
      visible: true, title: 'Do you want to De-Activate your account?', leftBtnText: 'NO', rightBtnText: 'YES',
      subtitle: 'This will disable your ability to log in.', rightBtnBorderColor: colors.popupDangerPrimary,
      onResume: () => onAccountDeactivate(),
      onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false}),
      leftBtnBackgroundColor: colors.unselect, rightBtnBackgroundColor: colors.btn_bg_color,
      rightBtnBorderWidth: 1, rightBtnColor: colors.popupDangerPrimary
    });
  };

  const onAccountDeactivate = () => {
    setCancelModalConfig({...cancelModalConfig, visible: false});
    setLoadingOverlay(true);

    ServiceClient.deactivateAccount().then(() => {
      Context.logout().then(() => {
        setTimeout(() => {
          if (Platform.OS === 'web') window.location.reload();
        }, 150);
        navigation.reset({index: 0, routes: [{name: StartingPage}]});
        setLoadingOverlay(false);
      });

    }).catch(err => {
      console.log('DeactivateAccount err', err);
      setLoadingOverlay(false);

      setCancelModalConfig({
        visible: true, subtitle: err.message || 'DeactivateAccount Error', title: 'Error', leftBtnText: 'OK',
        onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const requestEmailChange = () => {
    setLoadingOverlay(true);

    ServiceClient.requestEmailChange(formik.values.email).then(() => {
      Context.setIsSuccessMessageShow('Email Confirmation Sent\n\nIf you do not see email, check your "junk" or "spam" folder');
      setLoadingOverlay(false);

      setTimeout(() => {
        Context.setIsSuccessMessageShow('');
      }, 3000);

    }).catch(err => {
      console.log('RequestEmailChange error', err);
      setLoadingOverlay(false);

      setCancelModalConfig({
        visible: true, subtitle: err.message || 'RequestEmailChange Error', title: 'Error', leftBtnText: 'OK',
        onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const updateUser = () => {
    if (!user) return;
    setLoadingOverlay(true);

    const {username, handedness, firstname, lastname, dateOfBirth, gender} = formik.values;
    const {allowTextNotifications, allowEmailNotifications, allowPartnerPromos, handicap} = user;

    const payload: updateUserPayloadType = {
      username, handedness, allowTextNotifications, allowEmailNotifications, allowPartnerPromos, handicap, firstname,
      lastname, dateOfBirth: dateOfBirth ? dateOfBirth.format(dateFormat) : '', gender
    };

    ServiceClient.updateUser(payload).then(() => {
      setLoadingOverlay(false);
      Context.getUser().then();

    }).catch(err => {
      console.log('UpdateUser error', err);
      setLoadingOverlay(false);

      setCancelModalConfig({
        visible: true, subtitle: err.message || 'UpdateUser Error', title: 'Error', leftBtnText: 'OK',
        onExit: () => setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const onSave = () => {
    if (!user) return;

    const {emailAddress, phoneNumber} = user;
    const {email, username, phone, handedness, firstname, lastname, dateOfBirth, gender} = formik.values;

    if (emailAddress !== email) requestEmailChange();
    if (phoneNumber !== phone) requestOtpCode();
    if (user.username !== username || user.handedness !== handedness || user.firstname !== firstname || user.lastname !== lastname || (dateOfBirth ? user.dateOfBirth !== dateOfBirth.format(dateFormat) : false) || user.gender !== gender) {
      updateUser();
    }
  };

  useLayoutEffect(() => {
    formik.resetForm({
      values: {
        email: user?.emailAddress || '', phone: user?.phoneNumber || '', username: user?.username || '',
        handedness: user?.handedness || '', firstname: user?.firstname || '', lastname: user?.lastname || '',
        dateOfBirth: user?.dateOfBirth ? moment(user.dateOfBirth, dateFormat) : null,
        gender: user?.gender || Gender.UNSPECIFIED
      }
    });

    // fixing issues with incorrect phone country
    setInit(true);
  }, []);

  return (
    <Div pt={isFullSwing ? 0 : 30} px={isFullSwing ? 0 : 15} maxW={690} mx="auto" w="100%" flex={1}>
      {isFullSwing ? (
        <>
          <LinearGradient colors={['rgba(78, 78, 78, 0.20)', 'rgba(78, 78, 78, 0.00)']} start={{x: 0.2, y: 0}}
                          end={{x: 0.8, y: 1}} style={styles.background}>
            <FullSwingProfileField formik={formik} label="Screen Name" name="username" divider/>
            <FullSwingProfileField
              formik={formik} label="First Name" name="firstname" divider readonly={isKycCompleted}
            />
            <FullSwingProfileField formik={formik} label="Last Name" name="lastname" readonly={isKycCompleted}/>
          </LinearGradient>

          <LinearGradient colors={['rgba(78, 78, 78, 0.20)', 'rgba(78, 78, 78, 0.00)']} start={{x: 0.2, y: 0}}
                          end={{x: 0.8, y: 1}} style={styles.background}>
            <FullSwingProfileField
              formik={formik} label="Date of Birth" name="dateOfBirth" divider readonly={isKycCompleted}
            />
            <FullSwingProfileField formik={formik} name="gender" label="Gender" divider readonly={isKycCompleted}/>
            <FullSwingProfileField formik={formik} label="Handedness" name="handedness"/>
          </LinearGradient>

          <LinearGradient colors={['rgba(78, 78, 78, 0.20)', 'rgba(78, 78, 78, 0.00)']} start={{x: 0.2, y: 0}}
                          end={{x: 0.8, y: 1}} style={styles.background}>
            <FullSwingProfileField formik={formik} label="Email" name="email" divider readonly/>
            <FullSwingProfileField formik={formik} label="Phone Number" name="phone" readonly/>

            {/*<Div row justifyContent="center" alignItems="center">*/}
            {/*  <P fontFamily="DavisSans-Regular" lh={56} mr={6}>Update Email or Phone Number</P>*/}
            {/*  <MaterialIcons name="open-in-new" size={20} color="white"/>*/}
            {/*</Div>*/}
          </LinearGradient>

          <Div flex={1}/>

          <Btn mb={34} mx={16} mt={16} bg="#3369FF" rounded={4} onPress={formik.submitForm}>
            <P lh={56} ta="center" fontFamily="DavisSans-Medium">Save</P>
          </Btn>
        </>
      ) : (
        <>
          <Button
            w={244} h={24} bg={colors.unselect} mb={19} fontSize={16} text="CHANGE ADDRESS"
            onPress={requestChange(ChangeType.ADDRESS)} mx="auto"
          />

          <Button
            w={244} h={24} bg={colors.unselect} fontSize={16} text="CHANGE PHOTO"
            onPress={requestChange(ChangeType.PHOTO)} mx="auto" mb={20}
          />

          <Input
            type="email-address" placeholder="Enter Text Here" label="EMAIL ADDRESS"
            value={formik.values.email} error={getFieldError('email', formik)} mb={9}
            onChangeText={formik.handleChange('email')} onBlur={formik.handleBlur('email')}
          />

          {init && (
            <PhoneInput
              placeholder="Phone Number" label="PHONE NUMBER - MOBILE *" mb={14}
              value={formik.values.phone} onChangeText={formik.handleChange('phone')}
              onBlur={formik.handleBlur('phone')} error={getFieldError('phone', formik)}
            />
          )}

          <Input
            placeholder="Enter Text Here" label="Player Name (for screen)"
            value={formik.values.username} error={getFieldError('username', formik)} mb={12}
            onChangeText={formik.handleChange('username')} onBlur={formik.handleBlur('username')}
          />

          <Handedness
            error={formik.touched.handedness && formik.errors.handedness} value={formik.values.handedness}
            onChange={val => formik.setFieldValue('handedness', val)} mb={30}
          />

          <Button text="UPDATE PROFILE" onPress={() => formik.submitForm()} mb={20}/>

          <Button
            bg="transparent" borderWidth={1} text="DEACTIVATE ACCOUNT" borderColor={colors.popupDangerPrimary}
            color={colors.popupDangerPrimary} onPress={deactivateAccountHandler} mb={25}
          />

          <Text style={styles.subtitle_s}>
            Any changes to your profile information must be reviewed and approved. You will receive an email after
            this process is complete.
          </Text>
        </>
      )}

      <OtpModal
        visible={phoneVerificationDialog} setVisible={updatePhoneNumber}
        phone={formik.values.phone} email={formik.values.email} goBackBtn
        onSuccess={() => navigation.reset({index: 0, routes: [{name: 'challenges'}]})}
      />

      <Loader visible={loadingOverlay}/>
      <CancelModal config={cancelModalConfig}/>
    </Div>
  );
};

const styles = {
  subtitle_s: [
    MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.c_white, MainStyle.t_a_c, MainStyle.w_267, MainStyle.m_x_auto,
    MainStyle.m_b_20
  ],
  background: [
    MainStyle.m_t_20, MainStyle.m_x_10, MainStyle.b_r_12, MainStyle.b_w_1, MainStyle.b_c_gray, MainStyle.z_i__1
  ]
};

export default React.memo(Profile);
